import { useEffect, useState } from "react"
import { FieldValues, useFieldArray, useForm, useWatch } from "react-hook-form"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"

import { showErrorToast } from "@fuse/sharedComponents/themeToast/toastUtils"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  Question,
  TDisplayFormat,
  TEventDetail,
  TTicket,
  TTicketForAttendee,
} from "@letshang/constants"
import { formatPhoneNumber, isPhoneNumberValid } from "@letshang/utils"
import { PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber"
import {
  castArray,
  compact,
  filter,
  find,
  flatten,
  get,
  groupBy,
  includes,
  isArray,
  isEmpty,
  isEqual,
  isNaN,
  isNumber,
  keyBy,
  last,
  map,
  mapValues,
  omit,
  size,
  sumBy,
  toNumber,
} from "lodash"
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil"
import { ROUTES } from "src/App"
import { useSessionStorage } from "usehooks-ts"
import * as yup from "yup"

import { reviewedBuddiesSelector } from "@fuse/recoil/buddies"
import { allEventCacheState, eventDetailsState } from "@fuse/recoil/events"
import { questionnaireFormIndex } from "@fuse/recoil/questionnaire"
import { userState } from "@fuse/recoil/signup"

import { processGroupTicketsAPI } from "@api/firebaseWrappedApis"
import { useCreatePaymentSheet } from "@api/services/events/useEventsApi"

import { WhichBtnState } from "@fuse/pages/eventPage/controller"
import useFetchEventDetails from "@fuse/pages/eventPage/useFetchEventDetails"
import { useBuyTicketsSession } from "@fuse/pages/eventSignUp/BuyTickets/buyTicketsSessionManager"
import { EBuyTicketSessionKeys } from "@fuse/pages/eventSignUp/BuyTickets/utils"

export type TicketFormData = {
  label: string
  friendId?: string | null
  name: string
  phoneNumber: string
  country: string
  countryPhoneCode: string
  ticketId: string
  isExpanded: boolean
}

// ✅ Validation Schema
const createTicketValidationSchema = () => {
  return yup.array().of(
    yup.object().shape({
      friendId: yup.string().nullable(),
      isQuestionnairesNeeded: yup.boolean(),

      name: yup.string().when("friendId", {
        is: (val) => !val,
        then: (schema) => schema.required("Name is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      phoneNumber: yup.string().when("friendId", {
        is: (val) => !val,
        then: (schema) =>
          schema
            .required("Phone number is required")
            .test("is-valid-phone", "Invalid phone number", function (value) {
              const country = this.parent.country
              return isPhoneNumberValid(value, country)
            }),
        otherwise: (schema) => schema.notRequired(),
      }),

      country: yup.string().when("friendId", {
        is: (val) => !val,
        then: (schema) => schema.required("Country is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      countryPhoneCode: yup.string().when("friendId", {
        is: (val) => !val,
        then: (schema) => schema.required("Country code is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      ticketId: yup.string().required(),
      isExpanded: yup.boolean(),
      attendeeQuestionersAnswer: yup.object().when("isQuestionnairesNeeded", {
        is: (val) => val,
        then: (schema) =>
          schema.test(
            "not-empty",
            "Attendee questionnaire answers are required",
            (value) => value && Object.keys(value).length > 0,
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
  )
}

// ✅ Default Values Generator (Without "attendees" Wrapper)
const generateDefaultValues = (tickets, user, baseQuestionnaires) => {
  const isSingleTicket = size(tickets) === 1

  const mappedTickets = tickets.flatMap((ticket) =>
    Array.from({ length: ticket.quantity }, (_, index) => {
      return {
        label: isSingleTicket
          ? ticket.ticketName
          : `${ticket.ticketName ?? ""} #${index + 1}`,
        friendId: isSingleTicket ? (user?.uid ?? "") : null,
        name: isSingleTicket ? (user?.displayName ?? "") : "",
        avatarUrl: isSingleTicket ? (user?.avatarUrl ?? "") : "",
        phoneNumber: isSingleTicket ? (user?.phoneNumber ?? "") : "",
        displayedPhoneNumber: "",
        country: user?.country ?? "",
        countryPhoneCode: user?.dial_code ?? "",
        ticketId: ticket.id,
        isExpanded: isSingleTicket,
        isMyTicket: false, //isSingleTicket,
        isManual: false,
        // isExtendAddOns: isSingleTicket,
        isExtendAddOns: true,
        ticketAddOns: get(ticket, "ticketAddOns", []).map((obj) => ({
          ...obj,
          isSelected: false,
        })),
        searchTerm: "",
        searchBuddyResult: [],
        isDetailsAdded: false,
        isCountryPicketModal: false,
        attendeeQuestioners: baseQuestionnaires,
        attendeeQuestionersAnswer: {},
        isQuestionnairesNeeded: size(baseQuestionnaires) > 0,
        ...ticket,
      }
    }),
  )

  return mappedTickets
}

export const useTicketFormController = () => {
  const params = useParams()
  const navigate = useNavigate()
  const eventId = get(params, "eventId", "")

  const { state: locationState } = useLocation()
  const { invitedId } = locationState
  const user = useRecoilValue(userState)
  const realBuddies = useRecoilValue(reviewedBuddiesSelector) || []
  const location = useLocation()
  const groupTicketsInfo = get(location, "state.groupTicketsInfo", [])

  const { setBuyTicketsSessionItem, getBuyTicketsSessionItem } =
    useBuyTicketsSession()

  const groupTicketsInfoState = getBuyTicketsSessionItem(
    EBuyTicketSessionKeys.GROUP_TICKETS_INFO_STATE,
  )
  // const [groupTicketsInfoState, setGroupTicketsInfoState] = useSessionStorage(
  //   EBuyTicketSessionKeys.GROUP_TICKETS_INFO_STATE,
  //   [],
  // )

  const ticketsToBuy = castArray(get(location, "state.ticketsToBuy", []))
  const validationSchema = createTicketValidationSchema()

  const [isLoading, setIsLoading] = useState(false)
  const [addOnsDetails, setAddOnsDetails] = useState({})

  const { state } = useFetchEventDetails(eventId)
  const eventLoading = get(state, "isLoading", false)
  const currentEventDetails = get(state, "eventDetails", {})
  const flyerImage = get(currentEventDetails, "eventDocData.flyerImage", "")
  const initiatorUid = get(currentEventDetails, "eventDocData.initiatorUid", "")
  const initiatorName = get(
    currentEventDetails,
    "eventDocData.initiatorName",
    "",
  )

  // const [storedTickets, setStoredTickets] = useSessionStorage<TicketFormData[]>(
  //   EBuyTicketSessionKeys.TICKETS_FORM_DATA,
  //   [],
  // )
  const storedTickets = getBuyTicketsSessionItem(
    EBuyTicketSessionKeys.TICKETS_FORM_DATA,
  )

  const baseQuestionnaires: Question[] = get(
    currentEventDetails,
    "eventDocData.questionnaires",
    [],
  )

  const answersToQuestions = get(location, "state.answersToQuestions", [])
  const questionersAttendeeIndex = get(
    location,
    "state.questionersAttendeeIndex",
    "",
  )

  const isQuestionnairesNeeded = size(baseQuestionnaires) > 0

  const phoneUtil = PhoneNumberUtil.getInstance()

  const areGroupTicketsInfoEqual = isEqual(
    groupTicketsInfo,
    groupTicketsInfoState,
  )

  useEffect(() => {
    if (isEmpty(groupTicketsInfo)) {
      // setGroupTicketsInfoState(groupTicketsInfo)
      setBuyTicketsSessionItem(
        EBuyTicketSessionKeys.GROUP_TICKETS_INFO_STATE,
        groupTicketsInfo,
      )
    } else {
      if (!areGroupTicketsInfoEqual) {
        // setGroupTicketsInfoState(groupTicketsInfo)
        setBuyTicketsSessionItem(
          EBuyTicketSessionKeys.GROUP_TICKETS_INFO_STATE,
          groupTicketsInfo,
        )
      }
    }
  }, [groupTicketsInfo])

  // changes
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitted },
    watch,
    setValue,
    trigger,
  } = useForm<FieldValues>({
    resolver: yupResolver(yup.object().shape({ tickets: validationSchema })),
    defaultValues: {
      tickets: isEmpty(storedTickets)
        ? generateDefaultValues(groupTicketsInfo, user, baseQuestionnaires)
        : areGroupTicketsInfoEqual
          ? storedTickets
          : generateDefaultValues(groupTicketsInfo, user, baseQuestionnaires),
    },
  })

  const { fields } = useFieldArray({
    control,
    name: "tickets",
  })

  const form = watch()
  const watchedTickets = useWatch({ control, name: "tickets" })

  useEffect(() => {
    if (!isEmpty(answersToQuestions)) {
      setValue(
        `tickets.${questionersAttendeeIndex}.attendeeQuestionersAnswer`,
        answersToQuestions,
      )
    }
  }, [answersToQuestions, questionersAttendeeIndex])

  useEffect(() => {
    if (watchedTickets) {
      // setStoredTickets(watchedTickets)
      setBuyTicketsSessionItem(
        EBuyTicketSessionKeys.TICKETS_FORM_DATA,
        watchedTickets,
      )
    }
  }, [watchedTickets])

  const isHaveMyTicket = !isEmpty(
    find(compact(get(form, "tickets", [])), ["isMyTicket", true]),
  )

  function addTicketAddOns(tickets, ticketDetails) {
    const groupByPhoneNumber = groupBy(ticketDetails, "phoneNumber")
    return tickets.map((ticket) => ({
      ...ticket,
      ...last(groupByPhoneNumber[get(ticket, "phoneNumber", "")]),
    }))
  }

  const onSubmit = (data) => {
    // setStoredTickets(data.tickets)
    setBuyTicketsSessionItem(
      EBuyTicketSessionKeys.TICKETS_FORM_DATA,
      data.tickets,
    )
    const tickets = get(data, "tickets", [])
    const nonRegisterUser = filter(tickets, function (value) {
      return isEmpty(get(value, "friendId", null))
    })

    const knownUserTickets = filter(tickets, function (value) {
      return !isEmpty(get(value, "friendId", null))
    })

    const validateNonRegisterUser = map(nonRegisterUser, function (obj) {
      return {
        name: get(obj, "name", ""),
        phoneNumber: get(obj, "phoneNumber", ""),
        country: get(obj, "country", ""),
        countryPhoneCode: get(obj, "countryPhoneCode", "").replace("+", ""),
        ticketId: get(obj, "ticketId", ""),
      }
    })

    if (isEmpty(validateNonRegisterUser)) {
      const mapKnownUserTickets = map(knownUserTickets, function (obj) {
        return {
          ...obj,
          attendeeId: get(obj, "friendId", ""),
        }
      })

      navigate(ROUTES.STRIPE_CHECKOUT.replace(":eventId", eventId), {
        state: { ticketsToBuy: mapKnownUserTickets, invitedId: invitedId },
      })
    } else {
      setIsLoading(true)
      processGroupTicketsAPI(validateNonRegisterUser)
        .then((result: any) => {
          const ticketsForNewUser = get(result, "tickets", [])

          const mapKnownUserTickets = map(knownUserTickets, function (obj) {
            return {
              ...obj,
              attendeeId: get(obj, "friendId", ""),
            }
          })

          const updatedTicketsForNewUser = addTicketAddOns(
            ticketsForNewUser,
            nonRegisterUser,
          )

          const validateUserTicket = flatten([
            ...mapKnownUserTickets,
            ...updatedTicketsForNewUser,
          ])

          const ticketGroup = groupBy(groupTicketsInfo, "id")

          const validateUserTickets = map(validateUserTicket, function (obj) {
            const ticketDetails = last(ticketGroup[get(obj, "ticketId", "")])
            return {
              ...ticketDetails,
              ...obj,
              attendeeId: get(obj, "attendeeId", get(obj, "friendId", "")),
            }
          })

          const totalTicketPrice = validateUserTickets.reduce((sum, ticket) => {
            return sum + (Number(ticket.price) || 0)
          }, 0)

          if (totalTicketPrice === 0) {
            onBuyFreeTicket(validateUserTickets)
          } else {
            navigate(ROUTES.STRIPE_CHECKOUT.replace(":eventId", eventId), {
              state: {
                ticketsToBuy: validateUserTickets,
                invitedId: invitedId,
              },
            })
          }
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }

  const {
    mutate,
    data: paymentSheetData,
    isLoading: paymentSheetLoading,
  } = useCreatePaymentSheet()

  const [cache, setCache] = useRecoilState(allEventCacheState)
  const reset = useResetRecoilState(eventDetailsState)

  const onBuyFreeTicket = (ticketsToBuy: TTicket[]) => {
    setIsLoading(true)
    const ticketsForAttendee: TTicketForAttendee[] = map(
      ticketsToBuy,
      function (obj) {
        const addOns = map(
          filter(get(obj, "ticketAddOns", []), ["isSelected", true]),
          function (obj) {
            return {
              ticketId: get(obj, "ticketId", ""),
            }
          },
        )

        return {
          ticketId: get(obj, "id", get(obj, "ticketId", "")),
          attendeeId: get(obj, "attendeeId", ""),
          amount: get(obj, "ticketPrice", 0),
          addOns: addOns,
          ...(!!invitedId && { invitedId: invitedId }),
        }
      },
    )
    return new Promise((resolve, reject) => {
      const ticketsAnswersToQuestions = mapValues(
        keyBy(ticketsToBuy, "attendeeId"),
        "attendeeQuestionersAnswer",
      )

      const payload = {
        eventId: eventId,
        questionnaires: ticketsAnswersToQuestions,
        tickets: ticketsForAttendee,
      }

      // Add coupon object only if both values are present

      mutate(payload, {
        onSuccess: (data) => {
          resolve(data)
          const eventDetails = currentEventDetails
          if (cache[eventId]) {
            setCache((prev) => ({
              ...prev,
              [eventId]: {
                ...prev[eventId], // preserve other fields like eventType, hasError
                eventDetails: {
                  ...prev[eventId]?.eventDetails, // preserve existing eventDetails
                  myJoinStatus: ticketsToBuy[0]?.requireApproval
                    ? WhichBtnState.UnderReview
                    : WhichBtnState.Accepted, // update only this field
                },
              },
            }))
          }
          reset()
          navigate(ROUTES.STRIPE_SUCCESS.replace(":eventId", eventId), {
            state: {
              eventDetails,
              requiredApproval: ticketsToBuy[0]?.requireApproval,
            },
          })
          // setTimeout(() => {
          //   refreshEventData()
          // }, 1200)
          setIsLoading(false)
        },
        onError: (err: any) => {
          // Handle error - you can perform any actions needed upon error here
          const errMsg = err.message.includes("following capabilities enabled")
            ? "The host needs to enable ticket purchases. Check back shortly!"
            : err.message
          showErrorToast(`Error: ${errMsg}`)
          reject(err)
          setIsLoading(false)
        },
      })
    })
  }

  const toggleExpanded = (index: number) => {
    const currentExpanded = get(form, `tickets.${index}.isExpanded`, false)
    setValue(`tickets.${index}.isExpanded`, !currentExpanded)
  }

  const toggleCountryPicker = (index) => {
    const isCountryPicketModal = get(
      form,
      `tickets.${index}.isCountryPicketModal`,
      false,
    )
    setValue(`tickets.${index}.isCountryPicketModal`, !isCountryPicketModal)
  }

  const onSearchBuddies = (searchTerm, index) => {
    const filteredBuddies = realBuddies.filter((buddy) => {
      return buddy.name.toLowerCase().includes(searchTerm.toLowerCase())
    })

    setValue(`tickets.${index}.searchBuddyResult`, filteredBuddies)
  }

  const onSelectCurrentUserTicket = (isMyTicket, index) => {
    const isHaveAddOns = !isEmpty(
      get(form, `tickets.${index}.ticketAddOns`, []),
    )

    if (!isMyTicket) {
      setValue(`tickets.${index}.friendId`, get(user, "uid", ""))
      setValue(`tickets.${index}.name`, get(user, "name", ""))
      setValue(`tickets.${index}.avatarUrl`, get(user, "avatarUrl", ""))
      if (!isHaveAddOns) {
        setValue(`tickets.${index}.isDetailsAdded`, true)
      } else {
        setValue(`tickets.${index}.isDetailsAdded`, true)
        setValue(`tickets.${index}.isExpanded`, true)
      }
      setValue(`tickets.${index}.isMyTicket`, true)
      setValue(`tickets.${index}.isManual`, false)
    } else {
      setValue(`tickets.${index}.friendId`, "")
      setValue(`tickets.${index}.name`, "")
      setValue(`tickets.${index}.avatarUrl`, "")
      if (!isHaveAddOns) {
        setValue(`tickets.${index}.isDetailsAdded`, false)
      }
      setValue(`tickets.${index}.isMyTicket`, !isMyTicket)
    }
    setValue(`tickets.${index}.displayedPhoneNumber`, "")
    setValue(`tickets.${index}.phoneNumber`, "")
    setValue(`tickets.${index}.searchTerm`, "")
    setValue(`tickets.${index}.searchBuddyResult`, [])
    if (!isHaveAddOns) {
      setValue(`tickets.${index}.isDetailsAdded`, true)
    }

    if (isSubmitted) {
      handleSubmit(onSubmit)()
    }
  }

  const onSelectBuddyAsAGuest = (buddy, index) => {
    const buddyId = get(buddy, "buddyId", "")
    const selectedGuest = compact(map(get(form, "tickets", []), "friendId"))

    if (buddyId === initiatorUid) {
      showErrorToast(
        `${initiatorName} is a host of the event. Try selecting someone else.`,
      )
    } else if (!includes(selectedGuest, buddyId)) {
      setValue(`tickets.${index}.friendId`, buddyId)
      setValue(`tickets.${index}.name`, get(buddy, "name", ""))
      setValue(`tickets.${index}.avatarUrl`, get(buddy, "avatarUrl", ""))
      setValue(`tickets.${index}.isDetailsAdded`, true)
      setValue(`tickets.${index}.isMyTicket`, false)

      setValue(`tickets.${index}.searchTerm`, get(buddy, "name", ""))
      onSearchBuddies(get(buddy, "name", ""), index)
      // setValue(`tickets.${index}.searchBuddyResult`, [])

      if (isSubmitted) {
        handleSubmit(onSubmit)()
      }
    } else {
      const buddyName = get(buddy, "name", "")
      showErrorToast(
        `${buddyName} already has a ticket. Try selecting someone else.`,
      )
    }
  }

  const canResetIsManualData = (value, index) => {
    if (!isEmpty(value)) {
      setValue(`tickets.${index}.friendId`, "")
      // setValue(`tickets.${index}.name`, "")
      setValue(`tickets.${index}.avatarUrl`, "")
      setValue(`tickets.${index}.isDetailsAdded`, false)
      setValue(`tickets.${index}.isMyTicket`, false)
    }
  }

  const getFormattedPhone = (digits, index) => {
    canResetIsManualData(digits, index)
    const country = get(form, `tickets.${index}.country`, "")

    const formatted = formatPhoneNumber(digits, country.code)
    const raw = formatted
      .replace("-", "")
      .replace("(", "")
      .replace(")", "")
      .replace(" ", "")
    setValue(`tickets.${index}.phoneNumber`, raw)

    trigger(`tickets.${index}.phoneNumber`)
    try {
      const inputNumber = String(digits || "").replace(/[^\d+]/g, "")
      if (!inputNumber) {
        console.error("Input number is empty or invalid", inputNumber)
        setValue(`tickets.${index}.displayedPhoneNumber`, String(digits || ""))
        return
      }

      // Validate the country code
      if (!country) {
        console.error("Country code is missing or invalid")
        setValue(`tickets.${index}.displayedPhoneNumber`, String(digits || ""))
        return
      }

      // Parse the number
      const parsedNumber = phoneUtil.parse(inputNumber, country)

      // Format the number in NATIONAL format
      let formattedDisplay = phoneUtil.format(
        parsedNumber,
        PhoneNumberFormat.NATIONAL,
      )

      // Remove leading '0' if present
      if (formattedDisplay.startsWith("0")) {
        formattedDisplay = formattedDisplay.replace(/^0/, "")
      }

      setValue(`tickets.${index}.displayedPhoneNumber`, formattedDisplay)
    } catch (error) {
      console.error("Error formatting phone number:", error)
      setValue(`tickets.${index}.displayedPhoneNumber`, String(digits || ""))
    }
  }

  const onAddManualDetails = (index) => {
    const isDetailsAdded = get(form, `tickets.${index}.isDetailsAdded`, false)
    const phoneNumber = get(form, `tickets.${index}.phoneNumber`, "")
    const country = get(form, `tickets.${index}.country`, "")

    if (!isPhoneNumberValid(phoneNumber, country)) {
      return showErrorToast("Invalid phone number")
    } else {
      setValue(`tickets.${index}.isDetailsAdded`, !isDetailsAdded)
    }
  }

  const onToggleItem = (index) => {
    const isDetailsAdded = get(form, `tickets.${index}.isDetailsAdded`, false)
    const isExpanded = get(form, `tickets.${index}.isExpanded`, false)
    const isManual = get(form, `tickets.${index}.isManual`, false)
    const friendId = get(form, `tickets.${index}.friendId`, null)

    if (isDetailsAdded) {
      setValue(`tickets.${index}.isDetailsAdded`, !isDetailsAdded)
      if (!isExpanded) {
        setValue(`tickets.${index}.isExpanded`, !isExpanded)
      }
    } else {
      if (isManual) {
        setValue(`tickets.${index}.isExpanded`, !isExpanded)
      } else {
        if (friendId) {
          setValue(`tickets.${index}.isDetailsAdded`, !isDetailsAdded)
          if (!isExpanded) {
            setValue(`tickets.${index}.isExpanded`, !isExpanded)
          }
        } else {
          setValue(`tickets.${index}.isExpanded`, !isExpanded)
        }
      }
    }
  }

  const setQuestionnaireFormIndex = useSetRecoilState(questionnaireFormIndex)

  const onFillQuestioners = (index) => {
    setQuestionnaireFormIndex(index)
    const ticket = get(form, `tickets.${index}`, {})

    const attendeeQuestionersAnswer = {}
    const attendeeQuestioners = baseQuestionnaires
      .map((question) => {
        if (question?.["dynamic"]) {
          const field = question?.["dynamic"]?.["rule"]?.["field"]
          const value = question?.["dynamic"]?.["rule"]?.["value"]
          const show = question?.["dynamic"]?.["rule"]?.["show"]
          const defaultValue = question?.["dynamic"]?.["default"]

          switch (field) {
            case "ticket":
              if (ticket?.id === value) {
                if (show) {
                  return question
                }
              }
              break

            case "addon": {
              const selectedAddOns = ticket.ticketAddOns?.filter(
                (addOn) => addOn.isSelected,
              )

              const isMatch = selectedAddOns?.some(
                (addOn) => addOn?.ticketId === value,
              )
              if (isMatch && show) {
                return question
              }

              break
            }

            default:
              break
          }

          attendeeQuestionersAnswer[question?.order] = defaultValue
        } else {
          return question
        }
      })
      .filter((question) => question)

    setValue(`tickets.${index}.attendeeQuestioners`, attendeeQuestioners)
    setValue(
      `tickets.${index}.attendeeQuestionersAnswer`,
      attendeeQuestionersAnswer,
    )

    let navToUpdated = false
    navToUpdated =
      get(currentEventDetails, "eventDocData.questionnaireDisplayFormat") ===
      TDisplayFormat.SINGLE_PAGE
    const navigationRoute = navToUpdated
      ? ROUTES.FILL_QUESTIONNAIRE_UPDATED
      : ROUTES.FILL_QUESTIONNAIRE
    navigate(navigationRoute.replace(":eventId", eventId), {
      state: {
        attendeeQuestioners,
        attendeeQuestionersAnswer,
        questionersAttendeeIndex: index,
        invitedId: invitedId,
        ticket,
      },
    })
  }

  const onAddInfoManually = (index) => {
    const isExpanded = get(form, `tickets.${index}.isExpanded`, false)
    if (!isExpanded) {
      setValue(`tickets.${index}.isExpanded`, true)
    }

    const searchTerm = get(form, `tickets.${index}.searchTerm`, "")

    if (isNumber(toNumber(searchTerm)) && !isNaN(toNumber(searchTerm))) {
      setValue(`tickets.${index}.name`, "")
      return getFormattedPhone(searchTerm, index)
    }

    setValue(`tickets.${index}.phoneNumber`, "")
    setValue(`tickets.${index}.displayedPhoneNumber`, "")
    return setValue(`tickets.${index}.name`, searchTerm)
  }

  const ticketsWithTotalPrice = sumBy(
    groupTicketsInfo,
    (ticket) => get(ticket, "quantity", 1) * get(ticket, "ticketPrice", 1),
  )

  return {
    eventId,
    isLoading: isLoading || eventLoading,
    flyerImage,
    register,
    control,
    handleSubmit,
    errors,
    form,
    fields,
    setValue,
    toggleExpanded,
    onSearchBuddies,
    onSelectBuddyAsAGuest,
    onSelectCurrentUserTicket,
    toggleCountryPicker,
    getFormattedPhone,
    onAddManualDetails,
    onToggleItem,
    canResetIsManualData,
    isHaveMyTicket,
    onSubmit,
    setAddOnsDetails,
    addOnsDetails,
    onFillQuestioners,
    isQuestionnairesNeeded,
    onAddInfoManually,
    userId: get(user, "uid", ""),
    ticketsWithTotalPrice,
  }
}
